.container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* Search row — wraps the SearchBox primitive. `padding: 12 16`
 * mirrors the Figma search wrapper (`py-12 px-16`). No background,
 * no border — the SearchBox carries its own pill chrome and the
 * row is a pure layout container. */
.searchRow {
  padding: var(--spacing-12) var(--spacing-16);
}

/* Actions row — Group checkbox on the left, action-button cluster
 * on the right. No top padding because the search row above already
 * supplies 12px. Matches Figma node 1108:2641. */
.actionsRow {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 var(--spacing-16) var(--spacing-12);
}

.actionsRight {
  display: flex;
  align-items: center;
  gap: var(--spacing-8);
}

/* Glance card slot. 16px side padding aligns the card with the `.list` gutter; the bottom gap lives
 * on the card child (`> *`), not the wrapper — `:empty` is defeated by React's conditional-render
 * comment nodes, so an empty wrapper would otherwise keep the bottom padding as phantom spacing. */
.recapSlot {
  padding: 0 var(--spacing-16);
}

.recapSlot > * {
  margin-bottom: var(--spacing-8);
}

/* The single scroll area: the recap card + the list (or empty state) scroll
 * together here, while the header/search/actions stay pinned above. Owns the
 * overflow that used to live on .list; the auto-mark-read observer uses this
 * element as its root (see useAutoMarkRead). */
.scrollArea {
  flex: 1;
  /* min-height: 0 lets this flex child shrink below its content so overflow-y scrolls (Safari's implicit min-height: auto otherwise breaks the scroll). */
  min-height: 0;
  overflow-y: auto;
  /* Trap overscroll here: without this, hitting the top/bottom edge chains the
   * scroll out to the visual viewport, which rubber-bands the whole page —
   * shifting the pinned header/footer and exposing the browser's white canvas
   * behind the app (jarring in dark theme). `contain` keeps the bounce inside
   * this element so the app frame stays put. */
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
}

/* Notification list — flex column with 8px gap between cards and
 * 16px horizontal inset per the Figma reference (node 1011:545).
 * See the popup list's comment for the raised-card rationale.
 *
 * Vertical padding rhythm: NONE. The `actionsRow` above provides
 * its own `padding-bottom: var(--spacing-12)` so the first card
 * sits 12px below the clear-all buttons. 12px bottom padding on
 * the list gives symmetric framing before the scroll container's
 * bottom edge — no footer, no border. Matches the Figma layout
 * which has no chrome below the list. */
.list {
  list-style: none;
  margin: 0;
  padding: 0 var(--spacing-16) var(--spacing-12);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-8);
}

/* Every direct `<li>` child of the list is a section (grouped-view
 * group, timeline bucket, or standalone call wrapper). Apply the
 * same flex-column gap-8 rhythm so cards inside a section stack
 * with the same 8px gap as between sections. Without this, cards
 * inside a bucket or group would stack as raw blocks with zero
 * gap — re-introducing the flat-list bug one level deeper.
 *
 * Using a child-combinator selector instead of per-class rules
 * (`.group`, `.bucket`) means single-card `<li>`s (like the call
 * wrappers) also get the rhythm for free; the flex layout is a
 * no-op for them but keeps the markup uniform. */
.list > li {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-8);
}

.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 48px var(--spacing-16);
  text-align: center;
  animation: fadeIn 0.3s ease-out;
}

.emptyIcon {
  color: var(--text-tertiary);
  margin-bottom: var(--spacing-12);
}

.emptyTitle {
  margin: 0 0 6px;
  font: var(--text-body-medium);
  color: var(--text-tertiary);
}

/* 13px literal: tokens-figma.css has no 13px font-size token. Same
 * pattern as the other 13px literals across the codebase. */
.emptyDescription {
  margin: 0;
  font-size: 13px;
  color: var(--text-tertiary);
}

/* Date separator (timeline mode). Sits at the top of each bucket
 * `<li>` as the first flex-column child, above the cards in that
 * bucket. No vertical padding — the parent bucket's flex gap-8
 * already provides rhythm between the separator and the first
 * card. No horizontal padding either — the list's own 16px inset
 * already positions the separator at the same edge as the cards. */
.dateSeparator {
  display: flex;
  align-items: center;
  padding: var(--spacing-4) 0;
  animation: fadeIn 0.25s ease-out;
}

.dateLabel {
  font-size: var(--text-size-xs);
  font-weight: var(--text-weight-medium);
  color: var(--text-tertiary);
  letter-spacing: 0.12px;
  white-space: nowrap;
}

/* App group — wrapping `<li>` around a group header and its nested
 * cards. Flex layout is inherited from the `.list > li` rule above;
 * this class is a semantic marker only. */
.group {
  /* intentionally empty — structural marker only */
}

/* Group header — sits at the top of each app group, inline with
 * its nested cards. No horizontal padding (the list's 16px inset
 * aligns the header with the cards beneath it). Minimal vertical
 * padding (4px) keeps the header tight against its cards while
 * still being tappable. No background — the raised cards below
 * are the visual anchor; a filled header bar would compete with
 * them. The collapse chevron + count pill carry the affordance. */
.groupHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-4) 0;
  cursor: pointer;
  user-select: none;
  border-radius: var(--radius-4);
  transition: background var(--motion-duration-fast);
}

/* --bg-tertiary (not --bg-secondary) so the hover is
 * distinguishable from the Notification cards stacked directly
 * below this header. Cards rest at --bg-secondary (#f5f5f5 /
 * #262626) — using the same token for the header hover would
 * erase the boundary between header and card in dark mode.
 * --bg-tertiary sits one step deeper (#e5e5e5 / #404040),
 * preserving a visible hover affordance in both themes. */
.groupHeader:hover {
  background: var(--bg-tertiary);
}

/* 13px literal: tokens-figma.css has no 13px font-size token
 * (adjacent sizes are --text-size-xs/12 and --text-size-s/14).
 * Keep as a literal until design ships one. Same pattern as
 * other 13px literals in PairingView. */
.groupName {
  font-size: 13px;
  font-weight: var(--text-weight-semibold);
  letter-spacing: 0.3px;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: var(--spacing-8);
}

.groupIcon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  border-radius: 4px;
}

.groupCount {
  font-size: 11px;
  font-weight: var(--text-weight-medium);
  color: var(--text-on-dark);
  background: var(--brand);
  border-radius: 10px;
  padding: 1px 7px;
  letter-spacing: 0;
}

.groupActions {
  display: flex;
  align-items: center;
  gap: var(--spacing-8);
}

/* .dismissAllBtn hover uses --icon-bg-hover — Figma's purpose-built
 * icon-button hover surface — instead of --stroke-neutral. */
.dismissAllBtn {
  font-size: 11px;
  color: var(--text-tertiary);
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: var(--radius-4);
  transition:
    background var(--motion-duration-fast),
    color var(--motion-duration-fast);
}

.dismissAllBtn:hover {
  background: var(--icon-bg-hover);
  color: var(--text-negative);
}

.chevron {
  color: var(--text-tertiary);
  width: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Root opt-in for keyboard nav (PRD docs/prd-keyboard.md §5.4).
 * The summon effect lands DOM focus on either a selected row
 * (`#kbd-row-${id}` via `row?.focus()`) or — when the inbox is
 * empty — on the column root itself. Neither path is in the
 * natural tab order, so suppressing the default focus ring is
 * intentional — the selection accent bar on the active row is the
 * visible cue.
 *
 * Exception: when the inbox is empty there's no row to paint an
 * accent bar on, so `:focus-visible` restores an outline on the
 * column root for keyboard-only summon. Pointer focus stays
 * outline-free. */
.kbdRoot {
  outline: none;
}
.kbdRoot:focus-visible {
  outline: 2px solid var(--brand);
  outline-offset: -2px;
}

/* Selection state: keyboard-enabled rows carry `aria-current="true"`
 * (set by the conditional spread in the JSX). Stack two visual cues
 * per PRD §4.2: a 3px brand accent bar on the leading edge (drawn
 * via inset box-shadow so the scroll container's `overflow: auto`
 * cannot clip it — a `::before` pseudo at `left: -8px` would be
 * silently truncated by horizontal overflow) and a background tint.
 * `aria-current` was chosen over `aria-selected` because the row
 * has no composite-widget role; ARIA 1.2 restricts `aria-selected`
 * to option/row/gridcell/tab/treeitem. */
.kbdRow {
  position: relative;
  border-radius: var(--radius-4);
  /* The `aria-current` accent bar + bg tint below ARE the selection
   * cue; the browser's default `:focus` ring on the tabIndex=0
   * wrapper would double up with a blue box around the whole card. */
  outline: none;
}
.kbdRow[aria-current="true"] {
  background: var(--bg-tertiary);
  box-shadow: inset 3px 0 0 var(--brand);
}

/* Animations */

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .empty,
  .dateSeparator {
    animation: none !important;
  }
}

/* Transient highlight when a Glance jump lands on a row; JS removes the class after ~2s.
   Brand-tinted flash that fades to transparent. */
.jumpHighlight {
  animation: jumpFlash 2s ease-out;
  border-radius: var(--radius-4);
}

@keyframes jumpFlash {
  from {
    background: color-mix(in srgb, var(--brand) 22%, transparent);
  }
  to {
    background: transparent;
  }
}

/* Reduced-motion: no flash; a brief static tint (the JS timeout still clears the class). */
@media (prefers-reduced-motion: reduce) {
  .jumpHighlight {
    animation: none;
    background: color-mix(in srgb, var(--brand) 16%, transparent);
  }
}
